import { parseTemporalValue } from './dateBucketFormat.js';

function pad2(n) {
  return String(n).padStart(2, '0');
}

const SEQUENTIAL_TIME_BUCKETS = new Set(['minute', 'hour', 'day', 'week', 'month', 'year']);

/** Eje X temporal donde tiene sentido rellenar huecos entre min y max. */
export function supportsFillTimeAxisGaps(dateBucket) {
  if (!dateBucket || dateBucket === 'raw') return true;
  return SEQUENTIAL_TIME_BUCKETS.has(dateBucket);
}

function resolveEffectiveTimeBucket(dateBucket, sampleValue) {
  if (dateBucket && dateBucket !== 'raw') return dateBucket;
  const s = String(sampleValue ?? '').trim();
  if (/:\d{2}:\d{2}/.test(s)) return 'minute';
  if (/\d{1,2}:\d{2}/.test(s)) return 'hour';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'day';
  if (/^\d{4}-\d{2}$/.test(s)) return 'month';
  if (/^\d{4}$/.test(s)) return 'year';
  const parsed = parseTemporalValue(sampleValue);
  if (parsed) {
    if (parsed.getUTCMinutes() !== 0 || parsed.getUTCSeconds() !== 0) return 'minute';
    if (parsed.getUTCHours() !== 0) return 'hour';
    return 'day';
  }
  return 'hour';
}

function floorToBucket(date, bucket) {
  const d = new Date(date.getTime());
  switch (bucket) {
    case 'minute':
      d.setUTCSeconds(0, 0);
      break;
    case 'hour':
      d.setUTCMinutes(0, 0, 0);
      break;
    case 'day':
    case 'week':
      d.setUTCHours(0, 0, 0, 0);
      break;
    case 'month':
      d.setUTCDate(1);
      d.setUTCHours(0, 0, 0, 0);
      break;
    case 'year':
      d.setUTCMonth(0, 1);
      d.setUTCHours(0, 0, 0, 0);
      break;
    default:
      d.setUTCMinutes(0, 0, 0);
      break;
  }
  return d;
}

function addTimeBucket(date, bucket) {
  const d = new Date(date.getTime());
  switch (bucket) {
    case 'minute':
      d.setUTCMinutes(d.getUTCMinutes() + 1);
      return d;
    case 'hour':
      d.setUTCHours(d.getUTCHours() + 1);
      return d;
    case 'day':
      d.setUTCDate(d.getUTCDate() + 1);
      return d;
    case 'week':
      d.setUTCDate(d.getUTCDate() + 7);
      return d;
    case 'month':
      d.setUTCMonth(d.getUTCMonth() + 1);
      return d;
    case 'year':
      d.setUTCFullYear(d.getUTCFullYear() + 1);
      return d;
    default:
      d.setUTCHours(d.getUTCHours() + 1);
      return d;
  }
}

/** Clave canónica del eje X alineada con la agrupación temporal del SQL. */
export function formatTimeAxisKey(date, dateBucket) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const y = date.getUTCFullYear();
  const mo = pad2(date.getUTCMonth() + 1);
  const d = pad2(date.getUTCDate());
  const hh = pad2(date.getUTCHours());
  const mm = pad2(date.getUTCMinutes());
  const ss = pad2(date.getUTCSeconds());
  switch (dateBucket) {
    case 'minute':
      return `${y}-${mo}-${d} ${hh}:${mm}`;
    case 'hour':
      return `${y}-${mo}-${d} ${hh}:00`;
    case 'day':
    case 'week':
      return `${y}-${mo}-${d}`;
    case 'month':
      return `${y}-${mo}`;
    case 'year':
      return String(y);
    default:
      if (date.getUTCSeconds() !== 0) return `${y}-${mo}-${d} ${hh}:${mm}:${ss}`;
      if (date.getUTCMinutes() !== 0 || date.getUTCHours() !== 0) return `${y}-${mo}-${d} ${hh}:${mm}`;
      return `${y}-${mo}-${d}`;
  }
}

export function normalizeTimeAxisKey(value, dateBucket) {
  const parsed = parseTemporalValue(value);
  if (!parsed) return String(value ?? '');
  const bucket = resolveEffectiveTimeBucket(dateBucket, value);
  return formatTimeAxisKey(floorToBucket(parsed, bucket), bucket) ?? String(value);
}

export function canFillTimeAxisGaps(dateBucket, keys) {
  if (dateBucket === 'hourOfDay' || dateBucket === 'dayOfWeek') return false;
  if (!keys?.length) return false;
  const bucket = resolveEffectiveTimeBucket(dateBucket, keys[0]);
  if (!SEQUENTIAL_TIME_BUCKETS.has(bucket)) return false;
  return keys.some((k) => parseTemporalValue(k) != null);
}

/** Genera todas las marcas temporales entre la primera y la última presentes en los datos. */
export function expandTimeAxisKeys(keys, dateBucket, { maxBuckets = 10000 } = {}) {
  if (!keys?.length) return [];
  const effectiveBucket = resolveEffectiveTimeBucket(dateBucket, keys[0]);
  const dated = keys
    .map((k) => {
      const parsed = parseTemporalValue(k);
      if (!parsed) return null;
      const floored = floorToBucket(parsed, effectiveBucket);
      return { date: floored, key: formatTimeAxisKey(floored, effectiveBucket) };
    })
    .filter(Boolean);
  if (!dated.length) return keys;

  let min = dated[0].date;
  let max = dated[0].date;
  for (const item of dated) {
    if (item.date < min) min = item.date;
    if (item.date > max) max = item.date;
  }

  const result = [];
  for (let cursor = new Date(min.getTime()); cursor <= max; cursor = addTimeBucket(cursor, effectiveBucket)) {
    result.push(formatTimeAxisKey(cursor, effectiveBucket));
    if (result.length >= maxBuckets) break;
  }
  return result;
}
